import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return action.payload;
    case 'ADD_TO_CART':
      const existingItem = state.find(item => item.id === action.payload.id);
      if (existingItem) {
        return state.map(item =>
          item.id === action.payload.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...state, { ...action.payload, quantity: 1 }];
    case 'REMOVE_FROM_CART':
      return state.filter(item => item.id !== action.payload);
    case 'UPDATE_QUANTITY':
      if (action.payload.quantity < 1) return state;
      return state.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );
    case 'CLEAR_CART':
      return [];
    default:
      return state;
  }
};

const getCartKey = (userId) => `enerstic_cart_${userId}`;

const loadCartFromStorage = (userId) => {
  try {
    const cartData = localStorage.getItem(getCartKey(userId));
    return cartData ? JSON.parse(cartData) : [];
  } catch (error) {
    console.error('Error loading cart from storage:', error);
    return [];
  }
};

const saveCartToStorage = (userId, cart) => {
  try {
    localStorage.setItem(getCartKey(userId), JSON.stringify(cart));
    
    if (userId && userId !== 'guest') {
      fetch('http://localhost:5000/api/cart/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, cart })
      }).catch(err => console.log('Backend sync failed, using localStorage only'));
    }
  } catch (error) {
    console.error('Error saving cart to storage:', error);
  }
};

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, []);
  const { user, isLoaded } = useUser();

  useEffect(() => {
    if (isLoaded) {
      const userId = user ? user.id : 'guest';
      const savedCart = loadCartFromStorage(userId);
      dispatch({ type: 'SET_CART', payload: savedCart });
      
      if (user) {
        fetch(`http://localhost:5000/api/cart?userId=${user.id}`)
          .then(res => res.json())
          .then(backendCart => {
            if (backendCart && backendCart.length > 0) {
              dispatch({ type: 'SET_CART', payload: backendCart });
              saveCartToStorage(user.id, backendCart);
            }
          })
          .catch(err => console.log('Using localStorage cart, backend unavailable'));
      }
    }
  }, [user, isLoaded]);

  useEffect(() => {
    if (isLoaded && cart.length >= 0) {
      const userId = user ? user.id : 'guest';
      saveCartToStorage(userId, cart);
    }
  }, [cart, user, isLoaded]);

  const addToCart = (product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    if (user) {
      localStorage.removeItem(getCartKey(user.id));
    }
    dispatch({ type: 'CLEAR_CART' });
  };

  const totalCartPrice = cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalCartPrice
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};